import React, { useEffect, useMemo, useState } from 'react';
import Summary from './subcomponents/Summary';
import CardInfo from './subcomponents/CardInfo';
import BillingAddress from './subcomponents/BillingAddress';
import { useSelector } from 'react-redux';
import { BodyM } from 'components/Typography/Typography';
import { makeStyles } from '@material-ui/styles';
import styles from 'assets/jss/material-dashboard-pro-react/components/paymentFormStyle';
import PaymentService from 'services/payments/payment';
import ConditionalRender from 'components/ConditionalRender/ConditionalRender';
import Cards from './subcomponents/Cards';
import { IPaymentToken } from 'utils/api/models/payments/paymentTokens';
import SaveCardToggle from './subcomponents/SaveCardToggle';

// @ts-ignore
const useStyles = makeStyles(styles);

interface IPaymentFormProps {
	selectedDonation: types.payment.IDonation | null;
	totalAmount: number;
	formData: types.payment.IFormData;
	setFormData: types.payment.setFormData;
	setTokenData: (token: IPaymentToken) => void;
	toggleSaveCard: () => void;
	setSaveCard: (saveCard: boolean) => void;
	numberOfElements?: number;
	elementsText?: string;
	paymentLimitReached: types.payment.paymentLimitReached;
	checkValidity: boolean;
	validation: types.payment.IValidation;
	setValidation: React.Dispatch<React.SetStateAction<types.payment.IValidation>>;
	paymentError?: string | null;
}

const PaymentForm = ({
	selectedDonation,
	totalAmount,
	formData,
	setFormData,
	setTokenData,
	numberOfElements,
	elementsText,
	paymentLimitReached,
	checkValidity,
	validation,
	setValidation,
	toggleSaveCard,
	setSaveCard,
	paymentError,
}: IPaymentFormProps) => {
	const [showCvvInfo, setShowCvvInfo] = useState<boolean>(false);
	const [tokens, setTokens] = useState<IPaymentToken[]>([]);
	const classes = useStyles();

	const email = useSelector((state: any) => state.session.user.email);

	useEffect(() => {
		const request = PaymentService.getPaymentTokens().subscribe(({ tokens }) => {
			setTokens(tokens);
			if (!formData.number && tokens.length) {
				setTokenData(tokens[0]);
			}
		});

		return () => {
			request.unsubscribe();
		};
	}, [setTokenData]);

	useEffect(() => {
		setFormData('email', email);
		// eslint-disable-next-line
	}, [email]);

	const errors = useMemo(() => {
		const isInvalid = (field: keyof types.payment.IValidation) =>
			!validation[field].isPotentiallyValid || (checkValidity && !validation[field].isValid);
		return {
			number: isInvalid('number'),
			cvv: isInvalid('cvv'),
			date: isInvalid('date'),
			email: isInvalid('email'),
		};
	}, [validation, checkValidity]);

	return (
		<div className={classes.paymentContainer}>
			<Summary
				numberOfElements={numberOfElements}
				elementsText={elementsText}
				totalAmount={totalAmount}
				selectedDonation={selectedDonation}
			/>
			<div className={classes.cont}>
				<ConditionalRender
					show={tokens.length > 0}
					onTrue={() => (
						<Cards
							tokens={tokens}
							formData={formData}
							setFormData={setFormData}
							setTokenData={setTokenData}
							setSaveCard={setSaveCard}
							validation={validation}
							setValidation={setValidation}
						/>
					)}
					onFalse={() => (
						<CardInfo
							errors={errors}
							validation={validation}
							showCvvInfo={showCvvInfo}
							setShowCvvInfo={setShowCvvInfo}
							formData={formData}
							setFormData={setFormData}
							paymentLimitReached={paymentLimitReached}
							setValidation={setValidation}
						/>
					)}
				/>
				<BillingAddress
					setValidation={setValidation}
					formData={formData}
					setFormData={setFormData}
					validation={validation}
					errors={errors}
				/>
				<SaveCardToggle checked={!!formData.saveCard} toggle={toggleSaveCard} />
			</div>
			<BodyM color="danger">{paymentError}</BodyM>
		</div>
	);
};

export default PaymentForm;
